import { encrypt, decrypt } from "./crypto";

/**
 * Encrypts a parameters object into a single encrypted string for URL use
 * @param {Object} paramsObj - Object containing route parameters
 * @returns {string} - Encrypted string suitable for ?data= parameter
 */
export const encryptUrlParams = (paramsObj) => {
  if (!paramsObj || Object.keys(paramsObj).length === 0) {
    return "";
  }

  try {
    // Convert params object to JSON string
    const jsonString = JSON.stringify(paramsObj);

    // Encrypt the JSON string (encrypt function already handles encodeURIComponent)
    const encrypted = encrypt(jsonString);

    return encrypted || "";
  } catch (error) {
    console.error("Error encrypting URL params:", error);
    return "";
  }
};

/**
 * Decrypts an encrypted URL parameter string back to parameters object
 * @param {string} encryptedString - Encrypted string from ?data= parameter
 * @returns {Object} - Decrypted parameters object
 */
export const decryptUrlParams = (encryptedString) => {
  if (!encryptedString) {
    return {};
  }

  try {
    // Decrypt the string
    const decryptedString = decrypt(encryptedString);

    // Handle decryption errors
    if (!decryptedString || decryptedString === "Decryption error") {
      return {};
    }

    // Parse JSON back to object
    const paramsObj = JSON.parse(decryptedString);

    return paramsObj || {};
  } catch (error) {
    console.error("Error decrypting URL params:", error);
    return {};
  }
};

/**
 * Builds a complete URL with encrypted parameters
 * @param {string} baseUrl - Base URL without query parameters
 * @param {Object} paramsObj - Object containing route parameters
 * @returns {string} - Complete URL with encrypted ?data= parameter
 */
export const buildEncryptedUrl = (baseUrl, paramsObj) => {
  const encryptedData = encryptUrlParams(paramsObj);

  if (!encryptedData) {
    return baseUrl;
  }

  // Remove trailing slash if present
  const cleanBaseUrl = baseUrl.replace(/\/$/, "");

  // Add encrypted data parameter
  return `${cleanBaseUrl}?data=${encryptedData}`;
};

/**
 * Parses URL query string and decrypts if data parameter exists
 * @param {string} urlSearchString - URL search string (window.location.search)
 * @returns {Object} - Decrypted parameters object or empty object if no data param
 */
export const parseEncryptedUrl = (urlSearchString) => {
  if (!urlSearchString) {
    return {};
  }

  try {
    // Remove leading ?
    const cleanSearch = urlSearchString.replace(/^\?/, "");

    // Parse query string
    const params = {};
    cleanSearch.split("&").forEach((param) => {
      const [key, value] = param.split("=");
      if (key && value) {
        params[key] = decodeURIComponent(value);
      }
    });

    // Check if data parameter exists
    if (params.data) {
      // Decrypt and return
      return decryptUrlParams(params.data);
    }

    // No data parameter, return empty object (backward compatibility)
    return {};
  } catch (error) {
    console.error("Error parsing encrypted URL:", error);
    return {};
  }
};
